import React, { useState } from "react";
import { getFirestore, collection, getDocs, query,  } from "firebase/firestore";
import Button from "@mui/joy/Button";
import { toast } from "react-toastify";
import { FirestoreTimestampToDate, FirestoreTimestamp } from "../../utils/utils";
import IosShareIcon from '@mui/icons-material/IosShare';

interface Location {
  name: string;
}

interface Registration {
  event: string;
  name?: string;
  registeredAt: FirestoreTimestamp;
  useremail: string;
  username: string;
  points: number;
  organization?: string;
  phoneNumber?: string;
  city?: Location;
  country?: Location;
  state?: Location;
  agreeToTerms?: boolean;
}

interface ProcessedRegistration {
  event: string;
  name: string;
  registeredAt: string;
  useremail: string;
  username: string;
  points: number;
  organization: string;
  phoneNumber: string;
  city: string;
  country: string;
  state: string;
  agreeToTerms: boolean;
}

interface ExportButtonProps {
  eventName: string;
}

const ExportButton: React.FC<ExportButtonProps> = ({ eventName }) => {
  const [loading, setLoading] = useState(false);
  const exportToCSV = async () => {
    if (!eventName) return;

    setLoading(true);
    try {
      const db = getFirestore();
      // Create the path correctly using the events collection
      const eventsPath = `Events/${eventName}/Registrations`;

      const registrationsRef = collection(db, eventsPath);

      // Create a query to get all documents
      const q = query(registrationsRef);
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        toast.error("No registrations found");
        return;
      }

      const data = querySnapshot.docs.map((doc) => {
        const registration = doc.data() as Registration;
        const registeredAtString = FirestoreTimestampToDate(registration.registeredAt);
        return {
          event: registration.event || "eventNotProvided",
          name: registration?.name || "N/A",
          registeredAt: registeredAtString,
          useremail: registration.useremail || "emailNotProvided",
          username: registration.username || "usernameNotProvided",
          points: registration.points || 0,
          organization: registration?.organization || "organizationNotProvided",
          phoneNumber: registration?.phoneNumber || "phoneNumberNotProvided",
          city: registration?.city?.name || "N/A",
          country: registration?.country?.name || "N/A",
          state: registration?.state?.name || "N/A",
          agreeToTerms: registration?.agreeToTerms || false,
        } as ProcessedRegistration;
      });
      const headers = [
        "event",
        "name",
        "registeredAt",
        "useremail",
        "username",
        "points",
        "organization",
        "phoneNumber",
        "city",
        "country",
        "state",
        "agreeToTerms",
      ] as const;
      type Header = (typeof headers)[number];

      const csvContent = [
        headers.join(","),
        ...data.map((row) =>
          headers
            .map((header) => {
              const value = row[header as Header];
              return typeof value === "string" && value.includes(",")
                ? `"${value}"`
                : value;
            })
            .join(",")
        ),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `${eventName}_registrations.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      toast.error("Failed to export registrations");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant="outlined"
      color="primary"
      loading={loading}
      onClick={exportToCSV}
      startDecorator={<IosShareIcon />}
      // sx={{ mr: 2 }}
    >
      {loading ? "Exporting..." : "Export Registrations"}
    </Button>
  );
};

export default ExportButton;
